import Session from '@aspendental/shared-components-web/lib/components/Session';
import { getSessionId } from '@aspendental/shared-utils/utils';
import { CacheProvider, type EmotionCache } from '@emotion/react';
import { Alert, Snackbar, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { NextPage } from 'next';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import { ReactElement, ReactNode, useEffect } from 'react';
import '@aspendental/shared-components-web/lib/fonts.css';
import '@contentful/live-preview/style.css';
import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { Layout } from '@src/components/templates/layout';
import { useVirtualPageView } from '@src/hooks/useVirtualPageView';
import { getLogger } from '@src/lib/logging/logger';
import { isSentryEnabled, toggleSentry } from '@src/services/sentry';
import { useAppStore } from '@src/store';
import theme from '@src/theme/theme';
import createEmotionCache from '@src/utils/createEmotionCache';
import { initializeSentryClient } from '@src/utils/sentryClientInit';
import '../styles/globals.scss';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
// eslint-disable-next-line import/no-unresolved
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
import '../v2/App.css';
const clientSideEmotionCache = createEmotionCache();
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  emotionCache?: EmotionCache;
};
declare global {
  interface Window {
    dataLayer: any[];
  }
}
export function reportWebVitals({
  id,
  name,
  label,
  value
}: NextWebVitalsMetric) {
  const logger = getLogger('client');
  logger.info({
    name,
    category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    // values must be integers
    id // id unique to current page load
  });
}
const App = ({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache
}: AppPropsWithLayout) => {
  const router = useRouter();
  const config = pageProps?.config as BrandThemeConfig;
  const brandName = config?.name || 'WellNow Urgent Care';
  const {
    setTrainingStatus,
    training
  } = useAppStore();
  toggleSentry(!!pageProps?.config?.features?.sentry?.enabled);
  useVirtualPageView();
  const environment = pageProps.env?.ENVIRONMENT_NAME || process.env.NODE_ENV || 'development';
  useEffect(() => {
    if (isSentryEnabled()) {
      initializeSentryClient(environment);
      Sentry.setTag('brand', brandName);
      Sentry.setContext('user', {
        sessionId: getSessionId(),
        brand: brandName
      });
    }
  }, [brandName, environment]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setTrainingStatus(params.get('training') === '1');
  }, [setTrainingStatus, training]);
  const isErrorPage = ['/_error', '/404', '/500'].includes(router.pathname);
  const {
    features
  } = (pageProps.config ?? {}) as BrandThemeConfig;
  const getLayout = Component.getLayout ?? (page => {
    return <Layout locale={router.locale ?? 'en-US'} preview={pageProps.preview} appData={pageProps.appData} isFooterLite={features?.footer?.lite} withSimpleLayout={features?.footer?.withSimpleLayout} footerData={pageProps.footerData} logoHref={pageProps.env?.ENVIRONMENT_NAME === 'prod' ? pageProps.config?.features?.livePreview.prod : pageProps.config?.features?.livePreview.stage} pageProps={pageProps}>
					<>
						{training && <Snackbar open={true} anchorOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }} sx={theme => ({
          [theme.breakpoints.only('xs')]: {
            left: '20%',
            right: '20%'
          }
        })}>
								<Alert severity="info">
									<h3>
										You are in <strong>Training Mode</strong>
									</h3>
									<p>
										You can look around, but only book in the{' '}
										<a href="/schedule-appointment/?fc=WN_5328&training=1">Demo location</a>
									</p>
								</Alert>
							</Snackbar>}
						{page}
					</>
				</Layout>;
  });
  return <CacheProvider value={emotionCache} data-sentry-element="CacheProvider" data-sentry-component="App" data-sentry-source-file="_app.page.tsx">
			<Session data-sentry-element="Session" data-sentry-source-file="_app.page.tsx">
				<ContextProviders config={pageProps.config} preview={pageProps.preview} environment={pageProps.env?.ENVIRONMENT_NAME} user={pageProps.user} data-sentry-element="ContextProviders" data-sentry-source-file="_app.page.tsx">
					{isErrorPage ? getLayout(<Component {...pageProps} />) : <ThemeProvider theme={theme}>{getLayout(<Component {...pageProps} />)}</ThemeProvider>}
				</ContextProviders>
			</Session>
		</CacheProvider>;
};
export default appWithTranslation(App);